import { browser } from '$app/environment';
import type {
  CreateEligibilitySimulationDto,
  EligibilitySimulationView,
  UpdateEligibilitySimulationDto,
} from '$lib/utils/api-types';
import type { GeocodedResponse } from '$lib/utils/definitions';

import {
  defineHighestEligibilityZone,
  defineEligibleZone,
  steps,
  type ContractType,
  type DeclarationType,
  type EligibilityCategory,
  type EmploymentStatus,
  type HousingType,
  type Phase,
  type PositionType,
  type PropertySituation,
  type Step,
} from '$lib/utils/eligibility-simulator';
import { formattedThousandsToNumber } from '$lib/utils/formatters';

class EligibilitySimulator {
  public steps: Step[] = steps;

  public currentStep: Step = $state(this.steps[0]);
  public previousStep: Step | null = $derived.by(() => {
    if (this.currentStep.step < 2) {
      return null;
    } else {
      return this.steps[this.currentStep.step - 2];
    }
  });
  public nextStep: Step | null = $derived.by(() => {
    if (this.currentStep.step >= this.steps.length) {
      return null;
    } else {
      return this.steps[this.currentStep.step];
    }
  });

  public currentPhase: Phase = $state(this.steps[0].phases[0]);
  public previousPhase: Phase | null = $derived.by(() => {
    if (this.currentPhase.phase > 1) {
      return this.currentStep.phases[this.currentPhase.phase - 2];
    } else if (this.previousStep) {
      return this.previousStep.phases[this.previousStep.phases.length - 1];
    } else {
      return null;
    }
  });
  public nextPhase: Phase | null = $derived.by(() => {
    if (this.currentPhase.phase < this.currentStep.phases.length) {
      return this.currentStep.phases[this.currentPhase.phase];
    } else if (this.nextStep) {
      return this.nextStep?.phases[0];
    } else {
      return null;
    }
  });

  public loading: boolean = $state(false);
  public saveError: string | null = $state(null);

  // Household Composition
  public householdSize: number | undefined = $state(undefined);
  public selectedHouseholdSize: number | undefined = $derived.by(() => {
    if (!this.householdSize) {
      return undefined;
    } else if (this.householdSize < 7) {
      return this.householdSize;
    } else {
      return -1;
    }
  });
  public singlePersonInHousehold: boolean = $derived(
    this.selectedHouseholdSize === 1,
  );
  public twoToSixPersonsInHousehold: boolean = $derived(
    this.selectedHouseholdSize !== undefined &&
      this.selectedHouseholdSize >= 2 &&
      this.selectedHouseholdSize <= 6,
  );
  public moreThanSixPersonsInHousehold: boolean = $derived(
    this.selectedHouseholdSize === -1,
  );
  public hasDisability: boolean | undefined = $state(undefined);
  public propertySituation: PropertySituation | undefined = $state(undefined);
  public dependantsAmount: number | undefined = $state(undefined);
  public birthday: string | undefined = $state(undefined);
  public coBuyerBirthday: string | undefined = $state(undefined);

  // Fiscal Revenues
  public formattedTaxableIncome: string | undefined = $state(undefined);
  public declarationType: DeclarationType | undefined = $state(undefined);
  public firstCoBuyerFormattedTaxableIncome: string | undefined =
    $state(undefined);
  public secondCoBuyerFormattedTaxableIncome: string | undefined =
    $state(undefined);
  public taxableIncome: number | undefined = $derived.by(() => {
    if (
      this.firstCoBuyerFormattedTaxableIncome &&
      this.secondCoBuyerFormattedTaxableIncome
    ) {
      return (
        formattedThousandsToNumber(this.firstCoBuyerFormattedTaxableIncome) +
        formattedThousandsToNumber(this.secondCoBuyerFormattedTaxableIncome)
      );
    } else if (this.formattedTaxableIncome) {
      return formattedThousandsToNumber(this.formattedTaxableIncome);
    }

    return undefined;
  });
  public firstCoBuyerTaxableIncome: number | undefined = $derived.by(() => {
    if (this.firstCoBuyerFormattedTaxableIncome) {
      return formattedThousandsToNumber(
        this.firstCoBuyerFormattedTaxableIncome,
      );
    }

    return undefined;
  });
  public secondCoBuyerTaxableIncome: number | undefined = $derived.by(() => {
    if (this.secondCoBuyerFormattedTaxableIncome) {
      return formattedThousandsToNumber(
        this.secondCoBuyerFormattedTaxableIncome,
      );
    }

    return undefined;
  });

  // Eligibility
  public eligibility: EligibilityCategory | undefined = $derived.by(() => {
    if (
      typeof this.taxableIncome === 'number' &&
      typeof this.householdSize === 'number'
    ) {
      return defineEligibleZone(
        this.taxableIncome,
        this.householdSize,
        this.hasDisability || false,
        this.dependantsAmount,
        this.birthday,
        this.coBuyerBirthday,
      );
    }

    return undefined;
  });

  public highestEligibilityZone = $derived.by(() => {
    return defineHighestEligibilityZone(this.eligibility);
  });

  public eligibilityCategory = $derived.by(() => {
    return this.eligibility?.category;
  });

  // User details
  public firstName: string | undefined = $state(undefined);
  public lastName: string | undefined = $state(undefined);
  public email: string | undefined = $state(undefined);
  public phone: string | undefined = $state(undefined);
  public hasRefusedConnection: boolean = $state(false);

  // Search informations
  public selectedLocations: GeocodedResponse['properties'][] = $state([]);
  public housingType: HousingType | undefined = $state(undefined);

  // Financial informations
  public formattedContribution: string | undefined = $state(undefined);
  public formattedResources: string | undefined = $state(undefined);
  public contribution: number | undefined = $derived.by(() => {
    if (this.formattedContribution) {
      return formattedThousandsToNumber(this.formattedContribution);
    }

    return undefined;
  });
  public resources: number | undefined = $derived.by(() => {
    if (this.formattedResources) {
      return formattedThousandsToNumber(this.formattedResources);
    }

    return undefined;
  });

  // Additional informations
  public formattedHadBrsKnowledge: string | undefined = $state(undefined);
  public hadBrsKnowledge: boolean | undefined = $derived.by(() => {
    if (!this.formattedHadBrsKnowledge) return undefined;
    return this.formattedHadBrsKnowledge === 'true' ? true : false;
  });
  public employmentStatus: EmploymentStatus | undefined = $state(undefined);
  public laposteEmployer: string | undefined = $state(undefined);
  public canSendInformationsToLaposte: boolean | undefined = $state(undefined);
  public positionType: PositionType | undefined = $state(undefined);
  public positionStage: boolean | undefined = $state(undefined);
  public hasCompanyMoreThan10Employees: boolean | undefined = $state(undefined);
  public hasCompanyMoreThan50Employees: boolean | undefined = $state(undefined);
  public allowFinancingAndOwnershipAdvices: boolean | undefined =
    $state(undefined);
  public positionContractType: ContractType | undefined = $state(undefined);

  public eligibilitySimulation: EligibilitySimulationView | null = $state(null);

  public goToPreviousPhase = () => {
    if (this.hasRefusedConnection) {
      this.currentStep = this.steps[1];
      this.currentPhase = this.steps[1].phases[0];
      this.resetScroll();
    } else if (this.previousPhase) {
      const shouldGoToPreviousStep = this.currentPhase.phase === 1;

      this.currentPhase = this.previousPhase;

      if (shouldGoToPreviousStep && this.previousStep) {
        this.currentStep = this.previousStep;
      }

      this.resetScroll();
    }
  };

  public goToNextPhase = () => {
    if (this.nextPhase) {
      const shouldGoToNextStep =
        this.currentPhase.phase === this.currentStep.phases.length;

      this.currentPhase = this.nextPhase;

      if (shouldGoToNextStep && this.nextStep) {
        this.currentStep = this.nextStep;
      }

      this.resetScroll();
    }
  };

  public refuseConnection = () => {
    this.currentStep = this.steps[3];
    this.currentPhase = this.steps[3].phases[0];
    this.hasRefusedConnection = true;

    this.updateEligibilitySimulation({
      hasRefusedConnection: true,
    });

    this.resetScroll();
  };

  private resetScroll = () => {
    if (browser) {
      document.getElementById('simulateur')?.scrollIntoView({
        behavior: 'smooth',
        block: 'start',
      });
    }
  };

  public createEligibilitySimulation = async () => {
    this.loading = true;
    const payload: CreateEligibilitySimulationDto = {
      householdSize: this.householdSize as number,
      hasDisability: this.hasDisability as boolean,
      dependantsAmount: this.dependantsAmount as number,
      birthday: this.birthday as string,
      coBuyerBirthday: this.coBuyerBirthday as string,
    };

    const response = await fetch('/api/eligibility-simulations', {
      method: 'POST',
      body: JSON.stringify(payload),
    });

    const data = await response.json();

    if (!data.message) {
      this.eligibilitySimulation = data;
    }

    this.loading = false;
    this.goToNextPhase();
  };

  public updateEligibilitySimulation = async (
    payload: UpdateEligibilitySimulationDto,
  ) => {
    this.loading = true;
    this.saveError = null;

    try {
      const response = await fetch(
        `/api/eligibility-simulations/${this.eligibilitySimulation?.id}`,
        {
          method: 'PUT',
          body: JSON.stringify(payload),
        },
      );

      const data = await response.json();

      if (!response.ok || data.message) {
        this.saveError =
          'L’enregistrement a échoué. Veuillez réessayer pour continuer.';
        return;
      }

      this.eligibilitySimulation = data;
      this.goToNextPhase();
    } catch {
      this.saveError =
        'L’enregistrement a échoué. Veuillez réessayer pour continuer.';
    } finally {
      this.loading = false;
    }
  };
}

const eligibilitySimulatorManager = new EligibilitySimulator();
export default eligibilitySimulatorManager;
